import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction
} from "react";
import { useLocation } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  serializeArrayParam,
  deserializeArrayParam,
  serializeObjectArrayParam,
  deserializeObjectArrayParam
} from "shared/utils/search-params-serialization";
import { ApplicationStatus, ScreeningStatus } from "shared/types";
import { ActiveTab } from "../components/Table/constants";
import {
  DEFAULT_PER_PAGE,
  SESSION_STORAGE_KEY,
  URL_PARAM_KEYS,
  DEFAULT_URL_PARAMS
} from "../constants";

export interface ApplicationFilters {
  status?: ApplicationStatus[];
  properties?: { id: string; name: string }[];
  cities?: string[];
  units?: { id: string; name: string }[];
  unitTypes?: string[];
  screeningStatuses?: ScreeningStatus[];
  unitStatus?: "listed" | "unlisted";
  assigneeIds?: number[];
  propertyGroups?: { id: string; name: string }[];
}

interface UseApplicationQueryParamsReturn {
  filters: ApplicationFilters | undefined;
  searchParams: Record<string, string>;
  sorting: SortingState;
  groupBy: string | undefined;
  searchText: string;
  isInitialized: boolean;
  assigneeIds: number[] | null;
  setFilters: (
    filters:
      | ApplicationFilters
      | undefined
      | ((
          prev: ApplicationFilters | undefined
        ) => ApplicationFilters | undefined)
  ) => void;
  setSearchParams: (params: Record<string, string>) => void;
  setSorting: (sorting: SortingState) => void;
  setGroupBy: (groupBy: string | undefined) => void;
  setAssigneeIds: (assigneeIds: number[] | null) => void;
  handleSortingChange: Dispatch<SetStateAction<SortingState>>;
  handleRemoveProperty: (propertyId: string) => void;
  handleRemoveCity: (city: string) => void;
  handleRemoveUnit: (unitId: string) => void;
  handleRemoveUnitType: (unitType: string) => void;
  handleRemoveScreeningStatus: (status: ScreeningStatus) => void;
  handleRemoveUnitStatus: () => void;
  handleRemovePropertyGroup: (groupId: string) => void;
}

const parseFiltersFromParams = (
  params: Record<string, string>
): ApplicationFilters | undefined => {
  const status = deserializeArrayParam(params.status) as
    | ApplicationStatus[]
    | undefined;
  const properties = deserializeObjectArrayParam(params.properties);
  const cities = deserializeArrayParam(params.cities);
  const units = deserializeObjectArrayParam(params.units);
  const unitTypes = deserializeArrayParam(params.unit_types);
  const screeningStatuses = deserializeArrayParam(params.screening_statuses) as
    | ScreeningStatus[]
    | undefined;
  const unitStatus = params.unit_status as "listed" | "unlisted" | undefined;
  const assigneeIds = deserializeArrayParam(params.assignee_ids)?.map(Number);
  const propertyGroups = deserializeObjectArrayParam(params.property_group_ids);

  if (
    !status &&
    !properties &&
    !cities &&
    !units &&
    !unitTypes &&
    !screeningStatuses &&
    !unitStatus &&
    !assigneeIds &&
    !propertyGroups
  )
    return undefined;

  const result: ApplicationFilters = {};

  if (status) result.status = status;
  if (properties) result.properties = properties;
  if (cities) result.cities = cities;
  if (units) result.units = units;
  if (unitTypes) result.unitTypes = unitTypes;
  if (screeningStatuses) result.screeningStatuses = screeningStatuses;
  if (unitStatus) result.unitStatus = unitStatus;
  if (assigneeIds) result.assigneeIds = assigneeIds;
  if (propertyGroups) result.propertyGroups = propertyGroups;

  return result;
};

const parseSortingFromParams = (
  params: Record<string, string>
): SortingState => {
  const sortBy = params.sort_by;
  const sortDir = params.sort_dir;

  if (!sortBy) return [];
  return [{ id: sortBy, desc: sortDir === "desc" }];
};

export const useApplicationQueryParams =
  (): UseApplicationQueryParamsReturn => {
    const location = useLocation();

    // Synchronously check if URL already has params - if so, we're initialized
    const [isInitialized, setIsInitialized] = useState(() => {
      const currentParams = new URLSearchParams(window.location.search);
      return URL_PARAM_KEYS.some((key) => currentParams.get(key));
    });

    const [urlParams, setUrlParams] = useSearchParamsState(
      DEFAULT_URL_PARAMS,
      [...URL_PARAM_KEYS],
      { excludeEmptyValues: true }
    );

    // Restore from sessionStorage if URL has no params (on initial mount only)
    useEffect(() => {
      if (isInitialized) return;

      const savedQueryString = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (savedQueryString) {
        const restoredParams = new URLSearchParams(savedQueryString);
        const newParams = { ...DEFAULT_URL_PARAMS };
        URL_PARAM_KEYS.forEach((key) => {
          const value = restoredParams.get(key);
          if (value) newParams[key] = value;
        });
        setUrlParams(newParams);
      } else setUrlParams(DEFAULT_URL_PARAMS);

      setIsInitialized(true);
    }, []);

    // Save to sessionStorage when unmounting
    useEffect(() => {
      return () => {
        const currentSearch = location.search;
        if (currentSearch)
          sessionStorage.setItem(SESSION_STORAGE_KEY, currentSearch.slice(1)); // Remove leading '?'
      };
    }, [location.search]);

    // Parse current state from URL params
    const filters = useMemo(
      () => parseFiltersFromParams(urlParams),
      [urlParams]
    );

    const sorting = useMemo(
      () => parseSortingFromParams(urlParams),
      [urlParams]
    );

    const groupBy = useMemo(
      () => urlParams.group_by || undefined,
      [urlParams.group_by]
    );

    const assigneeIds = useMemo(
      () => deserializeArrayParam(urlParams.assignee_ids)?.map(Number) || null,
      [urlParams.assignee_ids]
    );

    // Maintain backward compatibility with existing searchParams structure
    const searchParams = useMemo(
      () => ({
        application: urlParams.search || "",
        page: urlParams.page || "1",
        tab: urlParams.tab || ActiveTab.Submitted,
        per_page: urlParams.per_page || DEFAULT_PER_PAGE
      }),
      [urlParams.search, urlParams.page, urlParams.tab, urlParams.per_page]
    );

    const [debouncedSearchText] = useDebouncedValue(
      searchParams.application,
      500
    );

    const setFilters = useCallback(
      (
        filtersOrUpdater:
          | ApplicationFilters
          | undefined
          | ((
              prev: ApplicationFilters | undefined
            ) => ApplicationFilters | undefined)
      ) => {
        const currentFilters = parseFiltersFromParams(urlParams);
        const newFilters =
          typeof filtersOrUpdater === "function"
            ? filtersOrUpdater(currentFilters)
            : filtersOrUpdater;

        setUrlParams({
          ...urlParams,
          status: serializeArrayParam(newFilters?.status),
          properties: serializeObjectArrayParam(newFilters?.properties),
          cities: serializeArrayParam(newFilters?.cities),
          units: serializeObjectArrayParam(newFilters?.units),
          unit_types: serializeArrayParam(newFilters?.unitTypes),
          screening_statuses: serializeArrayParam(
            newFilters?.screeningStatuses
          ),
          unit_status: newFilters?.unitStatus || "",
          property_group_ids: serializeObjectArrayParam(
            newFilters?.propertyGroups
          )
        });
      },
      [urlParams, setUrlParams]
    );

    const setSearchParamsWrapper = useCallback(
      (newParams: Record<string, string>) => {
        const updates: Record<string, string> = { ...urlParams };

        if ("application" in newParams)
          updates.search = newParams.application || "";

        if ("page" in newParams)
          updates.page = newParams.page === "1" ? "" : newParams.page || "";

        if ("tab" in newParams)
          updates.tab =
            newParams.tab === ActiveTab.Submitted ? "" : newParams.tab || "";

        if ("per_page" in newParams)
          updates.per_page =
            newParams.per_page === DEFAULT_PER_PAGE
              ? ""
              : newParams.per_page || "";

        setUrlParams(updates);
      },
      [urlParams, setUrlParams]
    );

    const setSorting = useCallback(
      (newSorting: SortingState) => {
        if (newSorting.length === 0)
          setUrlParams({
            ...urlParams,
            sort_by: "",
            sort_dir: ""
          });
        else {
          const sort = newSorting[0];
          setUrlParams({
            ...urlParams,
            sort_by: sort.id,
            sort_dir: sort.desc ? "desc" : "asc"
          });
        }
      },
      [urlParams, setUrlParams]
    );

    const handleSortingChange: Dispatch<SetStateAction<SortingState>> =
      useCallback(
        (newSorting) => {
          if (typeof newSorting === "function") {
            const currentSorting = parseSortingFromParams(urlParams);
            const next = newSorting(currentSorting).slice(-1);
            setSorting(next);
          } else {
            const next = newSorting.slice(-1);
            setSorting(next);
          }
        },
        [urlParams, setSorting]
      );

    const setGroupBy = useCallback(
      (newGroupBy: string | undefined) => {
        setUrlParams({
          ...urlParams,
          group_by: newGroupBy || ""
        });
      },
      [urlParams, setUrlParams]
    );

    const handleRemoveProperty = useCallback(
      (propertyId: string) => {
        setFilters((prev) => {
          if (!prev?.properties?.length) return prev;
          const filtered = prev.properties.filter((p) => p.id !== propertyId);
          return {
            ...prev,
            properties: filtered.length ? filtered : undefined
          };
        });
      },
      [setFilters]
    );

    const handleRemoveCity = useCallback(
      (city: string) => {
        setFilters((prev) => {
          if (!prev?.cities?.length) return prev;
          const filtered = prev.cities.filter((c) => c !== city);
          return { ...prev, cities: filtered.length ? filtered : undefined };
        });
      },
      [setFilters]
    );

    const handleRemoveUnit = useCallback(
      (unitId: string) => {
        setFilters((prev) => {
          if (!prev?.units?.length) return prev;
          const filtered = prev.units.filter((u) => u.id !== unitId);
          return { ...prev, units: filtered.length ? filtered : undefined };
        });
      },
      [setFilters]
    );

    const handleRemoveUnitType = useCallback(
      (unitType: string) => {
        setFilters((prev) => {
          if (!prev?.unitTypes?.length) return prev;
          const filtered = prev.unitTypes.filter((type) => type !== unitType);
          return {
            ...prev,
            unitTypes: filtered.length ? filtered : undefined
          };
        });
      },
      [setFilters]
    );

    const handleRemoveScreeningStatus = useCallback(
      (status: ScreeningStatus) => {
        setFilters((prev) => {
          if (!prev?.screeningStatuses?.length) return prev;
          const filtered = prev.screeningStatuses.filter((s) => s !== status);
          return {
            ...prev,
            screeningStatuses: filtered.length ? filtered : undefined
          };
        });
      },
      [setFilters]
    );

    const handleRemoveUnitStatus = useCallback(() => {
      setFilters((prev) => {
        if (!prev || !prev.unitStatus) return prev;
        return {
          ...prev,
          unitStatus: undefined
        };
      });
    }, [setFilters]);

    const handleRemovePropertyGroup = useCallback(
      (groupId: string) => {
        setFilters((prev) => {
          if (!prev?.propertyGroups?.length) return prev;
          const filtered = prev.propertyGroups.filter((g) => g.id !== groupId);
          return {
            ...prev,
            propertyGroups: filtered.length ? filtered : undefined
          };
        });
      },
      [setFilters]
    );

    const setAssigneeIds = useCallback(
      (newAssigneeIds: number[] | null) => {
        setUrlParams({
          ...urlParams,
          assignee_ids: newAssigneeIds
            ? serializeArrayParam(newAssigneeIds.map(String))
            : "",
          page: "1"
        });
      },
      [urlParams, setUrlParams]
    );

    return useMemo(
      (): UseApplicationQueryParamsReturn => ({
        filters,
        searchParams,
        sorting,
        groupBy,
        searchText: debouncedSearchText,
        isInitialized,
        assigneeIds,
        setFilters,
        setSearchParams: setSearchParamsWrapper,
        setSorting,
        setGroupBy,
        setAssigneeIds,
        handleSortingChange,
        handleRemoveProperty,
        handleRemoveCity,
        handleRemoveUnit,
        handleRemoveUnitType,
        handleRemoveScreeningStatus,
        handleRemoveUnitStatus,
        handleRemovePropertyGroup
      }),
      [
        filters,
        searchParams,
        sorting,
        groupBy,
        debouncedSearchText,
        isInitialized,
        assigneeIds,
        setFilters,
        setSearchParamsWrapper,
        setSorting,
        setGroupBy,
        setAssigneeIds,
        handleSortingChange,
        handleRemoveProperty,
        handleRemoveCity,
        handleRemoveUnit,
        handleRemoveUnitType,
        handleRemoveScreeningStatus,
        handleRemoveUnitStatus,
        handleRemovePropertyGroup
      ]
    );
  };
